import { Button, Icon, Tag, Text } from "@ttc3k/trekker";
import { Calendar } from "iconoir-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { es, fr } from "date-fns/locale";
import { Flex } from "styled-system/jsx";
import {
  EnumPromotionAvailabilityType,
  type EventManyQuery,
} from "gql/generated";
import { MultilingualLangKey } from "shared/types";
import { getMultilingualString } from "shared/utils";
import {
  formatPromotionDates,
  listPromotionDates,
} from "shared/utils/promotionHelpers";
import type { WidgetPromotion } from "widgets/shared/WidgetPromotionsContext";

const MAX_LISTED_DATES = 3;

type PromotionRowProps = {
  promotion: WidgetPromotion;
  lang?: MultilingualLangKey;
  onInfoClick: (promotionId: string) => void;
};

/** One promotion in the listing slideout: title, Promotion chip, dates, Info. */
const PromotionRow = ({ promotion, lang, onInfoClick }: PromotionRowProps) => {
  const { t, i18n } = useTranslation("widgets", {
    keyPrefix: "OPERATOR.DETAILS_SIDE_PANE.PROMOTIONS",
  });
  const locale =
    i18n.language === "fr" ? fr : i18n.language === "es" ? es : undefined;

  // "Sep 19 · 20 · 21" plus a separately coloured "+ 8 more", per the design
  const { dateText, moreCount } = useMemo(() => {
    const { availability } = promotion;
    const labels = { ongoing: t("ONGOING"), more: () => "" };
    const text = formatPromotionDates(
      availability,
      labels,
      locale,
      MAX_LISTED_DATES,
    ).trim();
    const count =
      availability.type === EnumPromotionAvailabilityType.SpecificDates
        ? Math.max(
            listPromotionDates(availability).length - MAX_LISTED_DATES,
            0,
          )
        : 0;
    return { dateText: text, moreCount: count };
  }, [promotion, locale, t]);

  return (
    <Flex
      flexDir="column"
      gap="200"
      p="300"
      bg="white"
      border="1px solid"
      borderColor="border.light"
      borderRadius="150"
      part="details__promotion-row"
    >
      <Flex justifyContent="space-between" alignItems="flex-start" gap="300">
        <Text visual="bodySemiBold" color="text.dark" lineClamp={2}>
          {getMultilingualString(promotion.title, lang)}
        </Text>
        <Tag visual="accent.purple" size="sm" flexShrink={0}>
          {t("BADGE")}
        </Tag>
      </Flex>
      {/* Dates sit on the bottom edge of the Info button */}
      <Flex justifyContent="space-between" alignItems="flex-end" gap="300">
        <Flex alignItems="center" gap="100" minW={0}>
          <Icon
            Element={Calendar}
            color="icon.light"
            width="16px"
            height="16px"
            flexShrink={0}
          />
          <Text visual="smallMedium" color="text.light" truncate>
            {dateText}
            {moreCount > 0 && (
              <Text as="span" visual="smallMedium" color="text.accent.blue.mid">
                {" "}
                {t("MORE", { count: moreCount })}
              </Text>
            )}
          </Text>
        </Flex>
        <Button
          visual="primary"
          mode="light"
          size="sm"
          type="button"
          flexShrink={0}
          part="details__promotion-info"
          onClick={() => onInfoClick(promotion._id)}
        >
          {t("INFO")}
        </Button>
      </Flex>
    </Flex>
  );
};

export type WidgetEvent = EventManyQuery["eventMany"][number];

type EventRowProps = {
  event: WidgetEvent;
  lang?: MultilingualLangKey;
  onInfoClick: (eventId: string) => void;
};

/** One upcoming event in the listing slideout, laid out like a promotion row. */
const EventRow = ({ event, lang, onInfoClick }: EventRowProps) => {
  const { t, i18n } = useTranslation("widgets", {
    keyPrefix: "OPERATOR.DETAILS_SIDE_PANE.PROMOTIONS",
  });
  const locale =
    i18n.language === "fr" ? fr : i18n.language === "es" ? es : undefined;

  // Upcoming occurrences read like specific promotion dates
  const { dateText, moreCount } = useMemo(() => {
    const dates = (event.occurrences ?? []).filter((d): d is string => !!d);
    const availability = {
      type: EnumPromotionAvailabilityType.SpecificDates,
      dates,
    };
    const text = formatPromotionDates(
      availability,
      { ongoing: "", more: () => "" },
      locale,
      MAX_LISTED_DATES,
    ).trim();
    return {
      dateText: text,
      moreCount: Math.max(dates.length - MAX_LISTED_DATES, 0),
    };
  }, [event, locale]);

  return (
    <Flex
      flexDir="column"
      gap="200"
      p="300"
      bg="white"
      border="1px solid"
      borderColor="border.light"
      borderRadius="150"
      part="details__event-row"
    >
      <Flex justifyContent="space-between" alignItems="flex-start" gap="300">
        <Text visual="bodySemiBold" color="text.dark" lineClamp={2}>
          {getMultilingualString(event.title, lang)}
        </Text>
        <Tag visual="accent.blue" size="sm" flexShrink={0}>
          {t("EVENT_BADGE")}
        </Tag>
      </Flex>
      {/* Dates sit on the bottom edge of the Info button */}
      <Flex justifyContent="space-between" alignItems="flex-end" gap="300">
        <Flex alignItems="center" gap="100" minW={0}>
          <Icon
            Element={Calendar}
            color="icon.light"
            width="16px"
            height="16px"
            flexShrink={0}
          />
          <Text visual="smallMedium" color="text.light" truncate>
            {dateText}
            {moreCount > 0 && (
              <Text as="span" visual="smallMedium" color="text.accent.blue.mid">
                {" "}
                {t("MORE", { count: moreCount })}
              </Text>
            )}
          </Text>
        </Flex>
        <Button
          visual="primary"
          mode="light"
          size="sm"
          type="button"
          flexShrink={0}
          part="details__event-info"
          onClick={() => onInfoClick(event._id)}
        >
          {t("INFO")}
        </Button>
      </Flex>
    </Flex>
  );
};

type PromotionsSectionProps = {
  promotions: WidgetPromotion[];
  /** Upcoming events, already sorted by next occurrence; listed after promotions. */
  events?: WidgetEvent[];
  lang?: MultilingualLangKey;
  onPromotionClick: (promotionId: string) => void;
  onEventClick?: (eventId: string) => void;
};

/**
 * "Promotions", "Events" or "Promotions & Events" block of the listing
 * slideout. Promotions come first, in first-date order; events follow.
 */
export const PromotionsSection = ({
  promotions,
  events = [],
  lang,
  onPromotionClick,
  onEventClick,
}: PromotionsSectionProps) => {
  const { t } = useTranslation("widgets", {
    keyPrefix: "OPERATOR.DETAILS_SIDE_PANE.PROMOTIONS",
  });
  const hasPromotions = promotions.length > 0;
  const hasEvents = events.length > 0;
  if (!hasPromotions && !hasEvents) return null;
  const titleKey =
    hasPromotions && hasEvents
      ? "TITLE_WITH_EVENTS"
      : hasEvents
        ? "TITLE_EVENTS"
        : "TITLE";
  return (
    <Flex flexDir="column" gap="150" part="details__promotions">
      <Text visual="bodySemiBold" part="details__promotions-title">
        {t(titleKey)}
      </Text>
      {promotions.map(promotion => (
        <PromotionRow
          key={promotion._id}
          promotion={promotion}
          lang={lang}
          onInfoClick={onPromotionClick}
        />
      ))}
      {events.map(event => (
        <EventRow
          key={event._id}
          event={event}
          lang={lang}
          onInfoClick={id => onEventClick?.(id)}
        />
      ))}
    </Flex>
  );
};
